import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricingRegion } from '../contexts/PricingRegionContext';
import { useCart } from '../contexts/CartContext';
import { Check, Star, Zap } from 'lucide-react';
import { getPlans } from '../services/api';
import PriceSkeleton from './PriceSkeleton';
import {
  createFallbackPlans,
  getPlanContent,
  getPricingCopy,
} from '../data/pricing';

const MonthlyPlans = ({ headingLevel = 'h2' }) => {
  const { isRTL, currentLang } = useLanguage();
  const {
    pricingRegion,
    isPricingReady,
    formatPrice,
    localizeItemPrice,
    trackPricingEvent,
  } = usePricingRegion();
  const { addPlan, selectedPlan } = useCart();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const copy = getPricingCopy(currentLang);
  const HeadingTag = headingLevel;

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setPlans(await getPlans());
      } catch (error) {
        console.error('Error loading plans:', error);
        setPlans(createFallbackPlans());
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, []);

  const getPlanDetails = (plan) => {
    const centralContent = getPlanContent(plan.name, currentLang);
    return {
      name:
        centralContent?.name ||
        plan.name_translations?.[currentLang] ||
        plan.name,
      description:
        centralContent?.description ||
        plan.description_translations?.[currentLang] ||
        plan.description ||
        '',
      features:
        centralContent?.features?.length
          ? centralContent.features
          : (plan.features || []).map(
              (feature) =>
                feature.text_translations?.[currentLang] ||
                feature.text ||
                feature
            ),
    };
  };

  const handleSelectPlan = (plan) => {
    const pricedPlan = localizeItemPrice(plan, 'MONTHLY_PLAN');
    const details = getPlanDetails(plan);
    addPlan({
      id: plan.id,
      canonicalName: plan.name,
      name: details.name,
      price: Number(pricedPlan.price || 0),
      basePriceUsd: Number(plan.price || 0),
      pricingRegion: pricingRegion.id,
      currencyCode: pricingRegion.currencyCode,
    });
    trackPricingEvent('monthly_plan_selected', {
      itemKey: plan.name,
      displayedAmount: Number(pricedPlan.price || 0),
    });
  };

  return (
    <section
      id="plans"
      className="relative overflow-hidden bg-[#0a0a0a] py-24 md:py-32"
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
    >
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#00FFD1]/3 rounded-full blur-[200px] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 glass-badge mb-6">
            <Zap size={16} />
            <span className="text-sm font-medium">{copy.required}</span>
          </div>
          <HeadingTag className="text-4xl md:text-5xl lg:text-[48px] font-semibold text-white leading-[1.1] tracking-[-0.02em] mb-4">
            {copy.plansTitle}
          </HeadingTag>
          <p className="text-white/60 text-lg md:text-xl max-w-2xl mx-auto">
            {copy.plansSubtitle}
          </p>
        </div>

        {loading || !isPricingReady ? (
          <PriceSkeleton cards={3} />
        ) : (
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {plans.map((plan, index) => {
              const details = getPlanDetails(plan);
              const pricedPlan = localizeItemPrice(plan, 'MONTHLY_PLAN');
              const isSelected = selectedPlan?.id === plan.id;

              return (
                <div
                  key={plan.id || index}
                  data-testid={`plan-card-${plan.name
                    ?.toLowerCase()
                    .replace(/\s+/g, '-')}`}
                  className={`relative group ${
                    plan.is_popular
                      ? 'glass-card-featured scale-105 z-10'
                      : 'glass-card'
                  } ${isSelected ? 'ring-2 ring-[#00FFD1]' : ''}`}
                  style={{ borderRadius: '16px' }}
                >
                  {plan.is_popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 glass-badge flex items-center gap-1 whitespace-nowrap">
                      <Star size={14} fill="currentColor" />
                      {copy.mostPopular}
                    </div>
                  )}

                  <div className="p-8 md:p-10 h-full flex flex-col">
                    <h3 className="text-xl font-semibold text-white/70 mb-3 tracking-wide">
                      {details.name}
                    </h3>
                    <p className="text-white/50 text-sm leading-relaxed min-h-[60px] mb-5">
                      {details.description}
                    </p>

                    <div className="mb-8 flex flex-wrap items-end gap-x-2 gap-y-1">
                      <span className="text-[#00FFD1] text-5xl md:text-6xl font-bold">
                        {formatPrice(pricedPlan.price)}
                      </span>
                      <span className="text-white/40 text-lg pb-2">
                        {copy.perMonth}
                      </span>
                    </div>

                    <ul className="space-y-4 mb-8 flex-1">
                      {details.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-start gap-3"
                        >
                          <Check
                            size={18}
                            className="text-[#00FFD1] mt-1 flex-shrink-0"
                          />
                          <span className="text-white/80 text-base">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handleSelectPlan(plan)}
                      data-testid={`select-plan-${plan.name
                        ?.toLowerCase()
                        .replace(/\s+/g, '-')}`}
                      className={`w-full ${
                        plan.is_popular
                          ? 'btn-glass-primary'
                          : 'btn-glass-secondary'
                      }`}
                    >
                      {isSelected ? copy.selected : copy.selectPlan}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default MonthlyPlans;
